import React, { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { bannerContainerStyle } from './styles.ts';

import { TelegramCustomStar } from '@/components/common/svg/telegram/TelegramCustomStar.tsx';
import CheckShape from '@/components/common/svg/shapes/CheckShape.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import {
  Gender,
  PaywallTypes,
  ProductInvoicePlacements,
  VideoState
} from '@/enums/apiEnums.ts';

const CrossIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const BENEFITS = [
  'paywall.benefits.content.see_who_liked_you',
  'paywall.benefits.content.priority_likes',
  'profile.premium_banner.weekly_reactions'
];

export const PremiumBanner: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { aboutMe, premium } = useProfileStore();
  const isFemale = aboutMe?.profile?.gender === Gender.FEMALE;
  const hasVideo =
    (aboutMe?.profile?.v2videos?.length ?? 0) > 0 &&
    !aboutMe?.profile?.v2videos?.[0].in_review &&
    aboutMe?.profile?.v2videos?.[0].state === VideoState.STATE_ACTIVE;

  const [paywallOpen, setPaywallOpen] = useState<boolean>(false);

  const closePaywall = () => setPaywallOpen(false);
  const openPaywall = () => setPaywallOpen(true);

  return (
    <>
      <div style={containerStyle} onClick={openPaywall}>
        <div style={contentContainerStyle}>
          <TelegramCustomStar
            gradientColors={['#ffffff', '#ffffff']}
            height={21}
            width={21}
          />
          <div style={textStyle}>{t('Go Premium')}</div>
        </div>

        <div style={buttonContainerStyle}>
          <button style={buttonStyle()}>
            {!premium.isExpired && premium.expireTime ? (
              <>
                {t('premium_active_until')}
                <b>
                  {new Date(
                    premium.expireTime.replace(' ', 'T')
                  ).toLocaleDateString(i18n.language, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </b>
              </>
            ) : (
              t('profile.premium_banner.get_access')
            )}
          </button>
        </div>

        <div style={itemsContainerStyle}>
          <div
            style={{
              ...tableRowStyle,
              width: 'auto',
              position: 'absolute',
              top: 0,
              left: 16,
              right: 16,
              transform: 'translateY(-50%)'
            }}
          >
            <span style={{ flex: 1 }} />
            <div style={{ ...iconCellStyle, marginLeft: 8 }}>
              {premium.isExpired && (
                <span style={myPlanBadgeStyle}>
                  {t('profile.premium_banner.my_plan')}
                </span>
              )}
            </div>
            <div style={iconCellStyle}>
              {!premium.isExpired && (
                <span style={myPlanBadgeStyle}>
                  {t('profile.premium_banner.my_plan')}
                </span>
              )}
            </div>
          </div>
          <div style={tableRowStyle}>
            <span style={{ flex: 1 }} />
            <span style={{ ...tableHeaderTextStyle, marginLeft: 8 }}>
              {t('profile.premium_banner.free')}
            </span>
            <span style={tableHeaderTextStyle}>
              {t('profile.premium_banner.premium')}
            </span>
          </div>
          {BENEFITS.map((benefitKey, index) => {
            const isFreeCheck = index === 0 && isFemale && hasVideo;

            return (
              <div key={benefitKey} style={tableRowStyle}>
                <span style={itemTextStyle}>{t(benefitKey)}</span>
                <div style={{ ...iconCellStyle, marginLeft: 8 }}>
                  {isFreeCheck ? (
                    <CheckShape height={14} width={14} strokeColor="#ffffff" />
                  ) : (
                    <CrossIcon />
                  )}
                </div>
                <div style={iconCellStyle}>
                  <CheckShape height={14} width={14} strokeColor="#ffffff" />
                </div>
              </div>
            );
          })}

          <div style={buttonContainerStyle}>
            <button
              style={{
                backgroundColor: '#6C226F',
                color: '#ffffff',
                fontSize: 12,
                fontWeight: 510,
                borderRadius: 24,
                width: '100%',
                border: '1px solid #ffffff'
              }}
            >
              {t('profile.premium_banner.all_benefits')}
            </button>
          </div>
        </div>
      </div>

      <Modal
        fullScreen
        scrollable
        open={paywallOpen}
        handleClose={closePaywall}
      >
        <PaywallPopup
          handleClose={closePaywall}
          type={PaywallTypes.TYPE_SUBSCRIPTION}
          placement={ProductInvoicePlacements.PROFILE_PAGE_GET_PREMIUM}
          prevHeaderTemplate="light"
        />
      </Modal>
    </>
  );
};

const containerStyle: CSSProperties = {
  ...bannerContainerStyle,
  backgroundColor: '#5A125D',
  width: 'calc(100% - 40px)',
  marginTop: 20,
  borderRadius: 20,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: '0 16px 16px 16px'
};

const contentContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 12,
  gap: 8,
  position: 'relative',
  zIndex: 2
};

const textStyle: CSSProperties = {
  color: '#FFFFFF',
  fontSize: 19,
  fontWeight: 700
};

const itemsContainerStyle: CSSProperties = {
  marginTop: 25,
  display: 'flex',
  color: '#ffffff',
  backgroundColor: '#6C226F',
  flexDirection: 'column',
  borderRadius: 20,
  width: '100%',
  padding: '12px 16px 16px 16px',
  gap: 12,
  boxSizing: 'border-box',
  position: 'relative'
};

const COLUMN_WIDTH = 60;

const tableRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%'
};

const tableHeaderTextStyle: CSSProperties = {
  width: COLUMN_WIDTH,
  color: '#ffffff',
  fontSize: 9,
  fontWeight: 510,
  textAlign: 'center',
  flexShrink: 0
};

const myPlanBadgeStyle: CSSProperties = {
  fontSize: 8,
  fontWeight: 510,
  color: '#ffffff',
  backgroundColor: '#8D4990',
  borderRadius: 10,
  padding: '2px 6px'
};

const iconCellStyle: CSSProperties = {
  width: COLUMN_WIDTH,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const itemTextStyle: CSSProperties = {
  color: '#ffffff',
  fontSize: 10,
  fontWeight: 510,
  flex: 1
};

const buttonContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  height: 32,
  marginTop: 10
};

export const buttonStyle = () => {
  const activeColor = '#000';

  return {
    backgroundColor: '#FFF',
    color: activeColor,
    fontSize: 14,
    fontWeight: 590,
    borderRadius: 24,
    width: '100%',
    cursor: 'pointer'
  };
};
